import { z } from 'zod';
import { pageSchema } from '../../http/pagination';

export const PHONE_PATTERN = /^\d{10,11}$/;

const phoneSearchSchema = z.string().regex(PHONE_PATTERN, 'Phone number must be 10-11 digits').optional();

// GET /customers のクエリパラメータ
// - パラメータなし: 全件取得（ドロップダウン用）
// - page（必須）+ 検索条件（任意）: ページネーション付き絞り込み（顧客一覧用）
export const listCustomersQuerySchema = z.union([
  z.object({}).strict(),
  z
    .object({
      page: pageSchema,
      name: z.string().max(100, 'Name must be 100 characters or less').optional(),
      phone: phoneSearchSchema,
      'mobile-phone': phoneSearchSchema,
    })
    .transform(({ 'mobile-phone': mobilePhone, ...rest }) => ({
      ...rest,
      mobilePhone,
    })),
]);
export type ListCustomersQuery = z.infer<typeof listCustomersQuerySchema>;

export const customerSchema = z.object({
  id: z.number(),
  name: z.string(),
  phone: z.string().nullable(),
  mobilePhone: z.string().nullable(),
});
export type Customer = z.infer<typeof customerSchema>;

// 入力データ内の重複表示用の拡張スキーマ
export const customerWithRowNumberSchema = customerSchema.extend({
  rowNumber: z.number().optional(),
});
export type CustomerWithRowNumber = z.infer<typeof customerWithRowNumberSchema>;

export const listCustomersResponseSchema = z.object({
  count: z.number(),
  customers: z.array(customerSchema),
});
export type ListCustomersResponse = z.infer<typeof listCustomersResponseSchema>;

const phoneInputSchema = z.string().regex(PHONE_PATTERN, 'Phone number must be 10-11 digits').nullable();

// 入力データ内の重複行を表す（IDなし、行番号あり）
export const duplicateInputRowSchema = z.object({
  name: z.string(),
  phone: phoneInputSchema,
  mobilePhone: phoneInputSchema,
  rowNumber: z.number(),
});
export type DuplicateInputRow = z.infer<typeof duplicateInputRowSchema>;

export const bulkCreateCustomerItemSchema = z
  .object({
    name: z.string().min(1, 'Name is required').max(100, 'Name must be 100 characters or less'),
    phone: phoneInputSchema,
    mobilePhone: phoneInputSchema,
    rowKey: z.number().int().nonnegative(),
  })
  .refine(data => !(data.phone === null && data.mobilePhone === null), {
    message: 'At least one of phone or mobilePhone must be provided',
    path: ['phone'],
  });
export type BulkCreateCustomerItem = z.infer<typeof bulkCreateCustomerItemSchema>;

export const bulkCreateCustomersBodySchema = z
  .array(bulkCreateCustomerItemSchema)
  .min(1, 'At least one customer is required')
  .max(10, 'customers must be 10 or less');
export type BulkCreateCustomersBody = z.infer<typeof bulkCreateCustomersBodySchema>;

export const bulkCreateCustomersResultSchema = z.discriminatedUnion('resultType', [
  // 成功 (警告なし)
  z.object({
    resultType: z.literal('success'),
    value: customerSchema,
    rowKey: z.number(),
  }),
  // 成功 (警告あり)
  z.object({
    resultType: z.literal('success-with-warning'),
    value: customerSchema,
    reason: z.enum(['phone-shared', 'same-name-exists']),
    existingRecords: z.array(customerWithRowNumberSchema),
    rowKey: z.number(),
  }),
  // エラー - 入力データ同士の重複
  z.object({
    resultType: z.literal('error-input-duplicate'),
    input: bulkCreateCustomerItemSchema,
    reason: z.enum(['exact-duplicate', 'same-person-update-needed', 'name-changed', 'mobile-duplicate']),
    duplicateRows: z.array(duplicateInputRowSchema),
    rowKey: z.number(),
  }),
  // エラー - DB既存レコードとの重複
  z.object({
    resultType: z.literal('error-existing-duplicate'),
    input: bulkCreateCustomerItemSchema,
    reason: z.enum(['exact-duplicate', 'same-person-update-needed', 'name-changed', 'mobile-duplicate', 'other-error']),
    existingRecords: z.array(customerSchema),
    rowKey: z.number(),
  }),
]);
export type BulkCreateCustomersResult = z.infer<typeof bulkCreateCustomersResultSchema>;

export const bulkCreateCustomersResponseSchema = z.object({
  summary: z.object({
    success: z.number(),
    error: z.number(),
  }),
  results: z.array(bulkCreateCustomersResultSchema),
});
export type BulkCreateCustomersResponse = z.infer<typeof bulkCreateCustomersResponseSchema>;

export const updateCustomerSchema = z
  .object({
    name: z.string().min(1, 'Name is required').max(100, 'Name must be 100 characters or less'),
    phone: phoneInputSchema,
    mobilePhone: phoneInputSchema,
  })
  .refine(data => !(data.phone === null && data.mobilePhone === null), {
    message: 'At least one of phone or mobilePhone must be provided',
    path: ['phone'],
  });
export type UpdateCustomerBody = z.infer<typeof updateCustomerSchema>;

export const updateCustomerResponseSchema = z.discriminatedUnion('resultType', [
  // 成功 (警告なし)
  z.object({
    resultType: z.literal('success'),
    value: customerSchema,
  }),
  // 成功 (警告あり)
  z.object({
    resultType: z.literal('success-with-warning'),
    value: customerSchema,
    reason: z.enum(['phone-shared', 'same-name-exists']),
    existingRecords: z.array(customerSchema),
  }),
  // エラー
  z.object({
    resultType: z.literal('error'),
    input: updateCustomerSchema,
    reason: z.enum(['exact-duplicate', 'same-person-update-needed', 'name-changed', 'mobile-duplicate']),
    existingRecords: z.array(customerSchema).optional(),
  }),
]);
export type UpdateCustomerResponse = z.infer<typeof updateCustomerResponseSchema>;
